import { BenchmarkData, LocationGroupData } from './types';

export interface ChipChipDiscountData {
  date: string;
  chipchipPrice?: number; // ChipChip sales price for this date
  orderCount?: number; // Number of ChipChip orders for this date
  [key: string]: string | number; // Dynamic keys for each market group comparison
}

export function calculateChipChipDiscounts(
  benchmarkData: BenchmarkData,
  comparisonType: 'min' | 'avg'
): ChipChipDiscountData[] {
  if (!benchmarkData.locationGroups.length || !benchmarkData.allDates.length) {
    return [];
  }

  // Find ChipChip sales data (API or Database)
  const chipchipData = benchmarkData.locationGroups.find(
    group => group.groupName.includes('ChipChip Sales')
  );

  if (!chipchipData) {
    return [];
  }

  // Create a date-to-price mapping for ChipChip data
  const chipchipPriceMap = new Map<string, number>();
  chipchipData.dates.forEach((date, index) => {
    const price = chipchipData.minPrices[index];
    if (price && price > 0) {
      chipchipPriceMap.set(date, price);
    }
  });

  // Create date-to-price mappings for all market groups
  const marketGroupMaps = new Map<string, Map<string, number>>();
  benchmarkData.locationGroups.forEach(group => {
    if (group.groupName.includes('ChipChip Sales')) return;
    
    const priceMap = new Map<string, number>();
    const groupPrices = comparisonType === 'min' ? group.minPrices : group.avgPrices;
    
    group.dates.forEach((date, index) => {
      const price = groupPrices[index];
      if (price && price > 0) {
        priceMap.set(date, price);
      }
    });
    
    marketGroupMaps.set(group.groupName, priceMap);
  });

  // Calculate discounts for each date where we have ChipChip data
  const discountData: ChipChipDiscountData[] = benchmarkData.allDates.map((date) => {
    const dataPoint: ChipChipDiscountData = { date };
    const chipchipPrice = chipchipPriceMap.get(date);

    // Sum the number of orders for this date from ChipChip's detailed data
    const dateIndex = chipchipData.dates.indexOf(date);
    if (dateIndex >= 0 && chipchipData.detailedData[dateIndex]) {
      dataPoint.orderCount = chipchipData.detailedData[dateIndex].reduce((sum, record) => {
        return sum + (record.num_order || 0);
      }, 0);
    } else {
      dataPoint.orderCount = 0;
    }

    // Store the ChipChip price for export purposes
    if (chipchipPrice) {
      dataPoint.chipchipPrice = chipchipPrice;
    }

    // Skip if no ChipChip price for this date
    if (!chipchipPrice) {
      return dataPoint;
    }

    // Compare with each other market group
    marketGroupMaps.forEach((priceMap, groupName) => {
      const groupPrice = priceMap.get(date);

      if (groupPrice && groupPrice > 0) {
        // Store the actual market price for export
        dataPoint[`${groupName}_price`] = groupPrice;
        
        // Calculate price advantage: (ChipChip Price - Market Price) / Market Price * 100
        // Negative = ChipChip is cheaper (advantage), Positive = ChipChip is more expensive
        const discountPercentage = ((chipchipPrice - groupPrice) / groupPrice) * 100;
        dataPoint[groupName] = Math.round(discountPercentage * 100) / 100; // Round to 2 decimal places
      }
    });

    return dataPoint;
  });

  return discountData;
}

export function getMarketGroupNames(benchmarkData: BenchmarkData): string[] {
  return benchmarkData.locationGroups
    .filter(group => !group.groupName.includes('ChipChip Sales'))
    .map(group => group.groupName);
}